import { Field, FieldError, FieldHint, FieldLabel } from "@nt/ui/field";
import { buttonVariants, inputVariants } from "@nt/ui/recipes";
import { Plus, X } from "lucide-react";
import { useState, type FormEvent } from "react";

import { Section } from "@/components/section";
import { messageOf } from "@/lib/api";
import type { Session } from "@/lib/queries";
import { useWrite } from "@/lib/queries";
import { countryName } from "@/lib/format";

/** The API keeps at most this many social profiles. */
export const MAX_SOCIALS = 5;

/** What `updateProfile` stores: trimmed addresses, blanks dropped. */
export function cleanSocials(rows: readonly string[]): string[] {
  return rows.map((s) => s.trim()).filter(Boolean);
}

/** One input per social profile; one empty row when there are none. */
export function socialRows(saved: readonly string[]): string[] {
  return saved.length > 0 ? saved.slice(0, MAX_SOCIALS) : [""];
}

function ReadOnlyFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-0.5">
      <dt className="text-compact text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground">{value}</dd>
    </div>
  );
}

/** The Settings › Profile tab. */
export function ProfileTab({
  session,
  readOnly,
}: {
  session: Session;
  readOnly: boolean;
}) {
  const p = session.partner;
  const profile = useWrite<unknown, unknown>("/api/partner/profile", [
    "session",
  ]);
  const [displayName, setDisplayName] = useState(p?.displayName ?? "");
  const [website, setWebsite] = useState(p?.website ?? "");
  const [socials, setSocials] = useState<string[]>(() =>
    socialRows(p?.socials ?? []),
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await profile.mutateAsync({
        displayName,
        website: website.trim() || null,
        socials: cleanSocials(socials),
      });
      setSaved(true);
    } catch (err) {
      setError(messageOf(err));
    }
  }

  /** "Saved." describes the last save, so any edit takes it away. */
  const edited =
    <T,>(apply: (value: T) => void) =>
    (value: T) => {
      setSaved(false);
      apply(value);
    };
  const editSocials = edited(setSocials);
  const setRow = (i: number, value: string) =>
    editSocials(socials.map((s, j) => (j === i ? value : s)));
  const removeRow = (i: number) => {
    const next = socials.filter((_, j) => j !== i);
    editSocials(next.length > 0 ? next : [""]);
  };

  return (
    <>
      <Section
        title="Profile"
        description="How we show you in the program, and where you promote us."
      >
        <form className="grid gap-5" onSubmit={(e) => void save(e)}>
          <Field>
            <FieldLabel htmlFor="dn">Display name</FieldLabel>
            <input
              id="dn"
              required
              disabled={readOnly}
              value={displayName}
              onChange={(e) => edited(setDisplayName)(e.target.value)}
              className={inputVariants()}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="web">Website</FieldLabel>
            <input
              id="web"
              inputMode="url"
              disabled={readOnly}
              placeholder="https://"
              value={website}
              onChange={(e) => edited(setWebsite)(e.target.value)}
              className={inputVariants()}
            />
          </Field>
          <fieldset className="grid gap-2" disabled={readOnly}>
            <legend className="mb-1.5 text-sm font-medium text-foreground">
              Social profiles
            </legend>
            <ul className="grid gap-2">
              {socials.map((s, i) => (
                <li key={i} className="flex items-center gap-2">
                  <input
                    id={`soc-${i}`}
                    inputMode="url"
                    aria-label={`Social profile ${i + 1}`}
                    placeholder="https://"
                    value={s}
                    onChange={(e) => setRow(i, e.target.value)}
                    className={inputVariants()}
                  />
                  {readOnly ? null : (
                    <button
                      type="button"
                      aria-label={`Remove social profile ${i + 1}`}
                      disabled={socials.length === 1 && !s}
                      className={buttonVariants({
                        variant: "danger-ghost",
                        size: "icon",
                      })}
                      onClick={() => removeRow(i)}
                    >
                      <X aria-hidden />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <FieldHint>
              Up to {MAX_SOCIALS}, each starting with https://.
            </FieldHint>
            {readOnly || socials.length >= MAX_SOCIALS ? null : (
              <div>
                <button
                  type="button"
                  className={buttonVariants({ variant: "ghost", size: "sm" })}
                  onClick={() => editSocials([...socials, ""])}
                >
                  <Plus aria-hidden />
                  Add a profile
                </button>
              </div>
            )}
          </fieldset>
          {error ? <FieldError role="alert">{error}</FieldError> : null}
          {saved ? (
            <p role="status" className="text-sm text-success">
              Saved.
            </p>
          ) : null}
          {readOnly ? (
            <p className="text-sm text-muted-foreground">
              Your account is read-only, so your profile cannot be changed.
              Write to support@nonresident.tax.
            </p>
          ) : (
            <div>
              <button
                type="submit"
                disabled={profile.isPending}
                className={buttonVariants()}
              >
                {profile.isPending ? "Saving…" : "Save profile"}
              </button>
            </div>
          )}
        </form>
      </Section>
      <Section
        title="Account details"
        description="Write to support@nonresident.tax to change these."
      >
        <dl className="grid gap-4 sm:grid-cols-3">
          <ReadOnlyFact label="Sign-in email" value={session.email} />
          <ReadOnlyFact
            label="Legal name"
            value={p?.legalName || "On file with support"}
          />
          <ReadOnlyFact
            label="Country"
            value={p?.country ? countryName(p.country) : "On file with support"}
          />
        </dl>
      </Section>
    </>
  );
}
